import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CHANGELOG_PREFERENCE_KEY = "changelog_last_seen_title";

export type ChangelogSections = {
    new?: string[];
    improvements?: string[];
    bugFixes?: string[];
};

export type ChangelogData = {
    title: string;
    date?: string;
    description: string;
    imageUrl: string;
    link: string;
    /** Optional in-app route for a "Try it now!" CTA (only for major features). */
    actionUrl?: string;
    sections?: ChangelogSections;
};

const fetchChangelog = async (): Promise<ChangelogData | undefined> => {
    const response = await fetch(`${window.origin}/changelog/latest`);
    if (!response.ok) {
        throw new Error(`Failed to fetch changelog: ${response.status}`);
    }
    return (await response.json()) as ChangelogData;
};

/**
 * Fetches the latest changelog entry and tracks whether the user has seen it.
 *
 * Uses the backend proxy (`GET /changelog/latest`) to avoid direct requests to
 * external CDNs that enterprise customers may not have allowlisted.
 *
 * "Last seen" state is persisted via the User Preferences API so it syncs across devices.
 */
export const useChangelog = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const {
        data: changelog,
        isLoading: isChangelogLoading,
        error,
    } = useQuery({
        queryKey: ["changelog", "latest"],
        queryFn: fetchChangelog,
        staleTime: 1_000 * 60 * 60, // 1 hour
        gcTime: 1_000 * 60 * 60 * 24, // 24 hours
        retry: 1,
        refetchOnWindowFocus: false,
        enabled,
    });

    const { data: lastSeenPref, isLoading: isPreferenceLoading } = useGetPreferenceByKey(CHANGELOG_PREFERENCE_KEY);
    const { mutate: setPreference } = useSetPreference();

    const lastSeenTitle = lastSeenPref?.value;

    const hasNewChangelog = useMemo(() => {
        if (!changelog?.title || isPreferenceLoading) {
            return false;
        }
        return changelog.title !== lastSeenTitle;
    }, [changelog?.title, lastSeenTitle, isPreferenceLoading]);

    const markAsSeen = useCallback(() => {
        if (!changelog?.title) {
            return;
        }
        setPreference({
            key: CHANGELOG_PREFERENCE_KEY,
            value: changelog.title,
        });
    }, [changelog?.title, setPreference]);

    return {
        changelog,
        hasNewChangelog,
        markAsSeen,
        isLoading: isChangelogLoading || isPreferenceLoading,
        error,
    };
};
